import Component from '@glimmer/component'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { modifier } from 'ember-modifier'
import { fn } from '@ember/helper'
import { later } from '@ember/runloop'

const trackStepElement = modifier<HTMLElement, [FlowStep]>(function (
  element,
  [step]
) {
  step.registerElement(element)
  return () => step.unregisterElement()
})

export default class StepContainer extends Component {
  @service flowManager: FlowManager

  get activeSteps() {
    return this.flowManager.activeFlow?.activeSteps ?? []
  }

  get activePageSettings() {
    return this.flowManager.activeFlow?.activePage?.settings ?? {}
  }

  /**
   * Registers a step with the flow manager
   *
   * @param step - The step to register
   * @param hooks - The lifecycle hooks for the step
   */
  @action
  register(step: FlowStep, hooks: Record<FlowHook, Function>) {
    this.flowManager.registerStep(step, hooks)
  }

  @action
  unregister(step: FlowStep) {
    this.flowManager.unregisterStep(step)
  }

  @action
  updateFlow() {
    this.flowManager.evaluateSteps()
  }

  /** Pages opt out by setting `autoScrollSteps: false`; anything else scrolls. */
  get autoScrollEnabled(): boolean {
    return (this.activePageSettings as ClientPageSettings).autoScrollSteps !== false
  }

  @action
  scrollToNextStep(step: FlowStep) {
    if (!this.autoScrollEnabled) return

    later(() => {
      this.flowManager.scrollToNextStep(step)
    }, 30)
  }

  <template>
    {{#each this.activeSteps as |step|}}
      <div class='contents' {{trackStepElement step}}>
        <step.component
          @register={{this.register}}
          @unregister={{this.unregister}}
          @updateFlow={{this.updateFlow}}
          @step={{step}}
          @stepSettings={{step.settings}}
          @pageSettings={{this.activePageSettings}}
          @extensionContext={{step.extensionContext}}
          @scrollToNextStep={{fn this.scrollToNextStep step}}
        />
      </div>
    {{/each}}
  </template>
}
